import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { createList, sendTextToChat, shareTask, updateList } from "./todoApi";
import { buildTaskText, chatTitle, collectPeople, errorText, getId, userName } from "./todoUtils";

/* Окна раздела «Задачи»: общий каркас, список (название и участники), выбор чата. */

const normalize = (value) => String(value || "").toLocaleLowerCase("ru-RU").replace(/\s+/g, " ").trim();

const stopEvent = (event) => event.stopPropagation();

/** Окно поверх страницы. Закрывается крестиком, клавишей Esc и нажатием мимо. */
export function TodoDialog({ title, onClose, children, footer = null, wide = false, busy = false }) {
    useEffect(() => {
        const onKey = (event) => {
            if (event.key === "Escape" && !busy) onClose?.();
        };
        const previous = document.body.style.overflow;

        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";

        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = previous;
        };
    }, [busy, onClose]);

    if (typeof document === "undefined") return null;

    return createPortal(
        <div
            className="todoDialog"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onMouseDown={(event) => {
                event.stopPropagation();
                if (event.target === event.currentTarget && !busy) onClose?.();
            }}
            // Окно может открываться изнутри сообщения чата. События в React
            // всплывают и через портал — без этого нажатия в окне доходили бы
            // до сообщения (выделение, меню по правой кнопке).
            onClick={stopEvent}
            onDoubleClick={stopEvent}
            onContextMenu={stopEvent}
            onPointerDown={stopEvent}
            onPointerMove={stopEvent}
            onPointerUp={stopEvent}
            onPointerCancel={stopEvent}
            onTouchStart={stopEvent}
            onDragStart={stopEvent}
            onKeyDown={stopEvent}
        >
            <div className={`todoDialog__panel ${wide ? "is-wide" : ""}`}>
                <div className="todoDialog__head">
                    <h3>{title}</h3>
                    <button type="button" onClick={() => !busy && onClose?.()} aria-label="Закрыть">✕</button>
                </div>
                <div className="todoDialog__body">{children}</div>
                {footer ? <div className="todoDialog__foot">{footer}</div> : null}
            </div>
        </div>,
        document.body
    );
}

/**
 * Новый список или настройка существующего: название и участники.
 * Участников выбирают из людей, с которыми есть чаты.
 */
export function TodoListModal({ list = null, chats = [], meId = "", onClose, onSaved }) {
    const editing = !!list?._id;
    const [title, setTitle] = useState(list?.title || "");
    const [query, setQuery] = useState("");
    const [memberIds, setMemberIds] = useState(
        () => (list?.members || []).map(getId).filter((id) => id && id !== String(meId))
    );
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const people = useMemo(
        () => collectPeople(chats, meId, list?.members || []),
        [chats, list, meId]
    );

    const visible = useMemo(() => {
        const clean = normalize(query);
        if (!clean) return people;
        return people.filter((user) => normalize(`${userName(user)} ${user.email || ""}`).includes(clean));
    }, [people, query]);

    const toggle = (id) => setMemberIds(
        (current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id])
    );

    const save = async () => {
        const clean = title.trim();
        if (!clean) {
            setError("Введите название списка");
            return;
        }

        setBusy(true);
        setError("");

        try {
            const saved = editing
                ? await updateList(list._id, list.isPersonal ? { title: clean } : { title: clean, memberIds })
                : await createList({ title: clean, memberIds });
            onSaved?.(saved);
        } catch (saveError) {
            setError(errorText(saveError, "Не удалось сохранить список"));
            setBusy(false);
        }
    };

    return (
        <TodoDialog
            title={editing ? "Список задач" : "Новый список"}
            onClose={onClose}
            busy={busy}
            footer={(
                <>
                    <button type="button" onClick={onClose} disabled={busy}>Отмена</button>
                    <button type="button" className="is-primary" onClick={save} disabled={busy || !title.trim()}>
                        {busy ? "Сохраняем…" : editing ? "Сохранить" : "Создать список"}
                    </button>
                </>
            )}
        >
            <label className="todoField">
                <span>Название</span>
                <input
                    ref={inputRef}
                    type="text"
                    value={title}
                    maxLength={120}
                    placeholder="Например: Цех, Закупки, Сайт"
                    onChange={(event) => setTitle(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") save();
                    }}
                />
            </label>

            {list?.isPersonal ? (
                <p className="todoDialog__hint">
                    Это личный список — его видите только вы. Чтобы работать вместе, создайте общий список.
                </p>
            ) : (
                <>
                    <div className="todoDialog__label">
                        Участники
                        <small>видят все задачи списка и могут их менять · выбрано: {memberIds.length}</small>
                    </div>

                    {people.length > 8 ? (
                        <input
                            type="search"
                            className="todoDialog__search"
                            value={query}
                            placeholder="Найти человека"
                            onChange={(event) => setQuery(event.target.value)}
                        />
                    ) : null}

                    <div className="todoDialog__people">
                        {visible.map((user) => {
                            const id = getId(user);
                            return (
                                <label key={id} className="todoDialog__person">
                                    <input type="checkbox" checked={memberIds.includes(id)} onChange={() => toggle(id)} />
                                    <span>
                                        <b>{userName(user)}</b>
                                        {user.email ? <small>{user.email}</small> : null}
                                    </span>
                                </label>
                            );
                        })}
                        {!visible.length ? (
                            <p className="todoDialog__hint">
                                {people.length
                                    ? "Никого не нашлось."
                                    : "Позвать можно тех, с кем у вас есть чат. Сначала напишите человеку в чате."}
                            </p>
                        ) : null}
                    </div>
                </>
            )}

            {error ? <div className="todoDialog__error" role="alert">{error}</div> : null}
        </TodoDialog>
    );
}

/**
 * Отправить задачу в чат: карточкой (участники чата смогут открыть задачу и
 * посмотреть её, но не менять) или обычным текстом.
 */
export function TodoChatPickerModal({ task, subtasks = [], chats = [], meId = "", onClose, onSent }) {
    const [mode, setMode] = useState("card");
    const [query, setQuery] = useState("");
    const [busyId, setBusyId] = useState("");
    const [error, setError] = useState("");

    const text = useMemo(
        () => buildTaskText(task, subtasks, (user) => userName(user, "", "")),
        [subtasks, task]
    );

    const visible = useMemo(() => {
        const clean = normalize(query);
        return (Array.isArray(chats) ? chats : [])
            .filter((chat) => chat?._id && chat.chatType !== "scene_guest")
            .filter((chat) => !clean || normalize(chatTitle(chat, meId)).includes(clean));
    }, [chats, meId, query]);

    const send = async (chat) => {
        if (busyId) return;
        setBusyId(String(chat._id));
        setError("");

        try {
            let updated = null;
            if (mode === "card") updated = await shareTask(task._id, chat._id);
            else await sendTextToChat(chat._id, text.slice(0, 8000));
            onSent?.({ chat, mode, task: updated });
        } catch (sendError) {
            setError(errorText(sendError, "Не удалось отправить"));
            setBusyId("");
        }
    };

    return (
        <TodoDialog title="Отправить задачу в чат" onClose={onClose} busy={!!busyId}>
            <div className="todoDialog__modes" role="radiogroup" aria-label="Как отправить">
                <label className={mode === "card" ? "is-active" : ""}>
                    <input type="radio" name="todo-share-mode" checked={mode === "card"} onChange={() => setMode("card")} />
                    <span>
                        <b>Карточкой</b>
                        <small>Задачу можно открыть из чата и посмотреть. Менять её могут только участники списка.</small>
                    </span>
                </label>
                <label className={mode === "text" ? "is-active" : ""}>
                    <input type="radio" name="todo-share-mode" checked={mode === "text"} onChange={() => setMode("text")} />
                    <span>
                        <b>Текстом</b>
                        <small>Название, описание, подзадачи и комментарии обычным сообщением.</small>
                    </span>
                </label>
            </div>

            <input
                type="search"
                className="todoDialog__search"
                value={query}
                placeholder="Найти чат"
                onChange={(event) => setQuery(event.target.value)}
            />

            <div className="todoDialog__people">
                {visible.map((chat) => (
                    <button
                        key={chat._id}
                        type="button"
                        className="todoDialog__chat"
                        disabled={!!busyId}
                        onClick={() => send(chat)}
                    >
                        <b>{chatTitle(chat, meId)}</b>
                        <small>{busyId === String(chat._id) ? "Отправляем…" : "Отправить"}</small>
                    </button>
                ))}
                {!visible.length ? <p className="todoDialog__hint">Чатов не нашлось.</p> : null}
            </div>

            {error ? <div className="todoDialog__error" role="alert">{error}</div> : null}
        </TodoDialog>
    );
}
